import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import { ClinicInfo } from "@/components/Contact";
import { EnquiryForm } from "@/components/EnquiryForm";
import { Accordion } from "@/components/Accordion";
import { OfferPill } from "@/components/OfferPill";
import { FAQS, GETTING_HERE, IMAGES, MAPS_EMBED, OFFER, SITE_URL, ADDRESS_LINE1, ADDRESS_LINE2 } from "@/data";
import { useSEO } from "@/hooks/useSEO";

const PHOTOS = [
  { src: IMAGES.shopfront, alt: "Glebe Park Dental shopfront on Coranderrk Street, Canberra City", span: "sm:col-span-2" },
  { src: IMAGES.reception, alt: "Reception at Glebe Park Dental" },
  { src: IMAGES.hallway, alt: "Hallway to the treatment rooms at Glebe Park Dental" },
  { src: IMAGES.street, alt: "Coranderrk Street entrance to Glebe Park Dental, next to Glebe Park", span: "sm:col-span-2" },
];

const JSON_LD = {
  "@context": "https://schema.org",
  "@type": "ContactPage",
  name: "Contact Glebe Park Dental",
  url: `${SITE_URL}/contact`,
  mainEntity: {
    "@type": "Dentist",
    name: "Glebe Park Dental",
    telephone: "+61 2 6295 9460",
    address: { "@type": "PostalAddress", streetAddress: ADDRESS_LINE1, addressLocality: "Canberra", addressRegion: "ACT", postalCode: "2601", addressCountry: "AU" },
  },
};

const PhotoGrid = () => (
  <div data-testid="contact-photo-grid" className="grid grid-cols-1 gap-4 sm:grid-cols-2">
    {PHOTOS.map((p, i) => (
      <Reveal key={p.src} delay={0.06 * i} className={p.span || ""}>
        <div className="overflow-hidden rounded-2xl border border-white/10">
          <img src={p.src} alt={p.alt} loading="lazy" className={`w-full object-cover ${p.span ? "aspect-[21/9]" : "aspect-[4/3]"}`} />
        </div>
      </Reveal>
    ))}
  </div>
);

export default function ContactPage() {
  useSEO({
    title: "Contact & Find Us | Glebe Park Dental, Coranderrk St Canberra City",
    description: `Glebe Park Dental — ${ADDRESS_LINE1}, ${ADDRESS_LINE2}. Call (02) 6295 9460, book online, or send an enquiry. Opening hours, parking and directions — next to Glebe Park, near the Canberra Centre.`,
    path: "/contact",
    jsonLd: JSON_LD,
  });

  return (
    <main className="bg-cream" data-testid="contact-page">
      <Nav />

      <section className="relative overflow-hidden pb-16 pt-40 lg:pt-44">
        <img src={IMAGES.shopfront} alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-sage/80 via-sage/95 to-sage" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8 lg:px-16">
          <Reveal>
            <p className="font-mono text-xs tracking-[0.3em] uppercase text-leafmute">Come and see us</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-5 max-w-3xl font-serif text-4xl leading-[1.1] tracking-tight text-ink sm:text-5xl lg:text-6xl">
              Next to the park, <span className="italic text-fern">a short stroll from the Canberra Centre.</span>
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 max-w-xl font-sans text-sm leading-relaxed text-fern sm:text-base">
              Look for the green logo on the glass at {ADDRESS_LINE1}. Ring, book online, or send us a note below — reception will get back to you.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-8">
              <OfferPill testid="contact-offer-pill" light />
              <p data-testid="contact-offer-fine-print" className="mt-3 max-w-md font-sans text-xs leading-relaxed text-fern/80">{OFFER.fine}</p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="pb-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-16">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
            <Reveal className="lg:col-span-5">
              <ClinicInfo />
            </Reveal>
            <div className="flex flex-col gap-8 lg:col-span-7">
              <Reveal delay={0.1}>
                <div className="overflow-hidden rounded-2xl border border-leaf/15">
                  <iframe
                    title="Glebe Park Dental location — 15 Coranderrk Street, Canberra"
                    src={MAPS_EMBED}
                    className="h-72 w-full grayscale-[0.6] contrast-[1.05] sm:h-80"
                    loading="lazy"
                    data-testid="contact-page-map"
                  />
                </div>
              </Reveal>
              <Reveal delay={0.15}>
                <EnquiryForm />
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-pine py-24">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-14 px-5 sm:px-8 lg:grid-cols-12 lg:gap-16 lg:px-16">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="font-mono text-xs tracking-[0.3em] uppercase text-leaf">Getting here</p>
              <h2 className="mt-4 font-serif text-3xl tracking-tight text-cream sm:text-4xl">Easy to find, easy to get to.</h2>
            </Reveal>
            <dl data-testid="getting-here" className="mt-8 space-y-6">
              {GETTING_HERE.map((g, i) => (
                <Reveal key={g.k} delay={0.08 * i}>
                  <dt className="font-mono text-xs tracking-[0.25em] uppercase text-leaf">{g.k}</dt>
                  <dd className="mt-1.5 font-sans text-sm leading-relaxed text-mist sm:text-base">{g.v}</dd>
                </Reveal>
              ))}
            </dl>
          </div>
          <div className="lg:col-span-7">
            <PhotoGrid />
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <Reveal>
            <p className="text-center font-mono text-xs tracking-[0.3em] uppercase text-leafmute">Good questions</p>
            <h2 className="mt-4 text-center font-serif text-2xl text-ink sm:text-3xl">Things people usually ask us.</h2>
          </Reveal>
          <div className="mt-10">
            <Accordion items={FAQS} testid="contact-page-faq" itemTestid="contact-page-faq-item" dark={false} />
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
